import {
  VBCAlertRow,
  VBCAlertsV2Payload,
  VBCHospitalizationAlert,
  VBCQueryAlert,
  VBCRecommendedSpecialistVisitAlert,
  VBCSuspectDiagnosisAlert,
} from "../types/vbc-alerts";
import { extractVBCQueryCommentParts } from "./vbc-query-comment-utils";

const KIND_SORT_ORDER: Record<VBCAlertRow["kind"], number> = {
  QUERY: 0,
  SUSPECT_DIAGNOSIS: 1,
  HOSPITALIZATION: 2,
  SPECIALIST_VISIT: 3,
};

export const buildVBCAlertGroupKey = (alert: VBCAlertRow) => {
  // Rows are no longer expandable; keep each alert as its own group.
  return alert.id;
};

export const normalizeVBCAlertStatus = (
  status?: string | null
): VBCAlertRow["status"] => {
  const normalizedStatus = status?.trim().toUpperCase();
  if (normalizedStatus === "ACKNOWLEDGED" || normalizedStatus === "CLOSED") {
    return "ACKNOWLEDGED";
  }

  return "PENDING";
};

const pickAlertId = (item: Record<string, any> | null | undefined) => {
  const raw = item?.alertId ?? item?.alert_id ?? item?.alertID ?? null;
  if (raw === null || raw === undefined) {
    return undefined;
  }
  const value = String(raw).trim();
  return value ? value : undefined;
};

const pickAlertStatus = (item: Record<string, any> | null | undefined) =>
  item?.alertStatus ?? item?.alert_status ?? item?.status ?? null;

const getSortTimestamp = (value?: string | null) => {
  const timestamp = value ? Date.parse(value) : Number.NaN;
  return Number.isNaN(timestamp) ? 0 : timestamp;
};

const sortRows = (alerts: VBCAlertRow[]) =>
  alerts.sort((left, right) => {
    if (left.kind !== right.kind) {
      return KIND_SORT_ORDER[left.kind] - KIND_SORT_ORDER[right.kind];
    }

    if (left.kind === "QUERY" && right.kind === "QUERY") {
      const dosDiff = getSortTimestamp(right.dos) - getSortTimestamp(left.dos);

      if (dosDiff !== 0) {
        return dosDiff;
      }

      return left.icdCode.localeCompare(right.icdCode);
    }

    if (left.kind === "SUSPECT_DIAGNOSIS" && right.kind === "SUSPECT_DIAGNOSIS") {
      return (right.labResultDate || "").localeCompare(left.labResultDate || "");
    }

    if (left.kind === "SPECIALIST_VISIT" && right.kind === "SPECIALIST_VISIT") {
      return left.description.localeCompare(right.description);
    }

    if (left.kind === "HOSPITALIZATION" && right.kind === "HOSPITALIZATION") {
      const admissionDiff =
        getSortTimestamp(right.admissionDate) -
        getSortTimestamp(left.admissionDate);

      if (admissionDiff !== 0) {
        return admissionDiff;
      }

      const createdAtDiff =
        getSortTimestamp(right.createdAt) - getSortTimestamp(left.createdAt);

      if (createdAtDiff !== 0) {
        return createdAtDiff;
      }

      return left.description.localeCompare(right.description);
    }

    return 0;
  });

export const extractVBCAlertsV2 = (payload: any): VBCAlertsV2Payload => {
  const responseData =
    payload?.data && !Array.isArray(payload?.data) ? payload.data : payload;

  const suspectDiagnoses =
    responseData?.suspectDiagnoses ?? responseData?.suspect_diagnoses ?? [];
  const queries = responseData?.queries ?? [];
  const recommendedSpecialistVisits =
    responseData?.recommendedSpecialistVisits ??
    responseData?.recommended_specialist_visits ??
    [];
  const hospitalizations = responseData?.hospitalizations ?? [];

  return {
    suspectDiagnoses: Array.isArray(suspectDiagnoses) ? suspectDiagnoses : [],
    queries: Array.isArray(queries) ? queries : [],
    recommendedSpecialistVisits: Array.isArray(recommendedSpecialistVisits)
      ? recommendedSpecialistVisits
      : [],
    hospitalizations: Array.isArray(hospitalizations) ? hospitalizations : [],
  };
};

const buildSuspectDiagnosisRow = (
  diagnosis: VBCSuspectDiagnosisAlert,
  index: number
): VBCAlertRow => {
  const raw = diagnosis as VBCSuspectDiagnosisAlert & Record<string, any>;

  return {
    id: `suspect-${diagnosis.icdCode ?? "na"}-${diagnosis.labTest ?? "na"}-${diagnosis.labResultDate ?? "na"}-${index}`,
    kind: "SUSPECT_DIAGNOSIS",
    status: normalizeVBCAlertStatus(pickAlertStatus(raw)),
    alertId: pickAlertId(raw),
    icdCode: diagnosis.icdCode ?? "",
    icdCodeDescription: diagnosis.icdCodeDescription ?? "",
    v28: diagnosis.v28 ?? "",
    labTest: diagnosis.labTest ?? "",
    labValue: diagnosis.labValue ?? "",
    labResultDate: diagnosis.labResultDate ?? "",
    source: diagnosis.source ?? "",
  };
};

const buildQueryRow = (query: VBCQueryAlert, index: number): VBCAlertRow => {
  const raw = query as VBCQueryAlert & Record<string, any>;
  const { queryComment, queryEvidence, createdAt, dos } =
    extractVBCQueryCommentParts(query);

  return {
    id: `query-${query.icdCode ?? "na"}-${query.queryReason ?? "na"}-${index}`,
    kind: "QUERY",
    status: normalizeVBCAlertStatus(pickAlertStatus(raw)),
    alertId: pickAlertId(raw),
    icdCode: query.icdCode ?? "",
    icdCodeDescription: query.icdCodeDescription ?? "",
    v28: query.v28 ?? "",
    queryReason: query.queryReason ?? "",
    queryComment,
    queryEvidence,
    createdAt,
    dos,
  };
};

const buildSpecialistVisitRow = (
  visit: VBCRecommendedSpecialistVisitAlert,
  index: number
): VBCAlertRow => {
  const raw = visit as VBCRecommendedSpecialistVisitAlert & Record<string, any>;

  return {
    id: `specialist-${visit.code ?? "na"}-${visit.icdCode ?? "na"}-${index}`,
    kind: "SPECIALIST_VISIT",
    status: normalizeVBCAlertStatus(pickAlertStatus(raw)),
    alertId: pickAlertId(raw),
    icdCode: visit.icdCode ?? "",
    icdCodeDescription: visit.icdCodeDescription ?? "",
    v28: "",
    description: visit.displayName ?? "",
    createdAt: "",
  };
};

const buildHospitalizationRow = (
  hospitalization: VBCHospitalizationAlert,
  index: number
): VBCAlertRow => {
  const raw = hospitalization as VBCHospitalizationAlert & Record<string, any>;

  return {
    id: `hospitalization-${hospitalization.visitType ?? "na"}-${hospitalization.admissionDate ?? "na"}-${hospitalization.nextVisit ?? "na"}-${index}`,
    kind: "HOSPITALIZATION",
    status: normalizeVBCAlertStatus(pickAlertStatus(raw)),
    alertId: pickAlertId(raw),
    icdCode: "",
    icdCodeDescription: "",
    v28: "",
    description: hospitalization.visitType ?? hospitalization.description ?? "",
    createdAt: hospitalization.nextVisit ?? "",
    admissionDate: hospitalization.admissionDate ?? "",
    dischargeDate: hospitalization.dischargeDate ?? "",
    principalDiagnosis: hospitalization.principalDiagnosis ?? "",
  };
};

export const buildVBCAlertRows = (payload: VBCAlertsV2Payload): VBCAlertRow[] =>
  sortRows([
    ...payload.suspectDiagnoses.map(buildSuspectDiagnosisRow),
    ...payload.queries.map(buildQueryRow),
    ...payload.recommendedSpecialistVisits.map(buildSpecialistVisitRow),
    ...payload.hospitalizations.map(buildHospitalizationRow),
  ]);

export const countVisibleVBCAlertRows = (alerts: VBCAlertRow[]) =>
  new Set(
    alerts
      .filter((alert) => alert.status === "PENDING")
      .map(buildVBCAlertGroupKey)
  ).size;
